// src/components/DownloadExcelButton.js
import React, { useState } from "react";
import * as XLSX from "xlsx";
import "./DownloadExcelButton.css";
import config from "../../config.json";

const DOWNLOAD_PASSWORD = config.password;

// ------------------------------
// LABELS (normalize dog + cat)
// ------------------------------
const START_LABEL = "Start time:";
const END_LABEL = "End time:";

// Support duration label (keep as a Set for future variants)
const DURATION_LABELS = new Set(["Duration: (in minutes)"]);

const DOG_WALK_LOCATION_LABEL = "Location of walk:";
const CAT_LOCATION_LABEL = "Location:";

// ONE combined output column
const LOCATION_LABEL_OUT = "Location:";

const SPECIES_LABEL = "Species";
const SOCIAL_LABEL = "Socialized?";

// ------------------------------
// DATE HELPERS (MM/DD/YYYY w/ leading zeros)
// ------------------------------
function pad2(n) {
  const s = String(n);
  return s.length === 1 ? `0${s}` : s;
}

// Always returns "MM/DD/YYYY"
function toMMDDYYYY(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
}

// If entry.id contains a date, normalize it to MM/DD/YYYY
function normalizeDateKeyFromId(id) {
  if (!id) return "";
  const m = String(id).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return "";
  const mm = pad2(m[1]);
  const dd = pad2(m[2]);
  const yyyy = m[3];
  return `${mm}/${dd}/${yyyy}`;
}

// ------------------------------
// TIME HELPERS (start/end)
// ------------------------------
function parseTimeToDate(timeStr) {
  if (!timeStr) return null;
  const t = String(timeStr).trim();

  // 12-hour with AM/PM
  const m12 = t.match(/^(\d{1,2}):([0-5]\d)\s*(AM|PM)$/i);
  if (m12) {
    let h = +m12[1];
    const m = +m12[2];
    const ap = m12[3].toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return new Date(1970, 0, 1, h, m);
  }

  // 24-hour HH:MM
  const m24 = t.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (m24) return new Date(1970, 0, 1, +m24[1], +m24[2]);

  return null;
}

function formatTo12Hour(timeStr) {
  const d = parseTimeToDate(timeStr);
  return d
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : timeStr || "";
}

function computeEndTime(startStr, durationStr) {
  const d = parseTimeToDate(startStr);
  if (!d) return "";
  const mins = parseInt(durationStr, 10);
  if (!Number.isFinite(mins) || mins <= 0) return "";
  d.setMinutes(d.getMinutes() + mins);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// ------------------------------
// DATE KEY HELPER (OUTPUT CONSISTENT)
// ------------------------------
function getDateKeyFromEntry(entry) {
  const fromId = normalizeDateKeyFromId(entry?.id);
  if (fromId) return fromId;

  if (entry?.time) return toMMDDYYYY(entry.time);

  return "";
}

// ------------------------------
// NORMALIZE QUESTION LABELS
// ------------------------------
function normalizeQuestionLabel(label) {
  if (!label) return label;
  if (label === DOG_WALK_LOCATION_LABEL) return LOCATION_LABEL_OUT;
  if (label === CAT_LOCATION_LABEL) return LOCATION_LABEL_OUT;
  return label;
}

// ------------------------------
// Get entries: dogs -> walks, cats -> notes
// ------------------------------
function getEntries(animal, species) {
  if (species === "Dog") return Array.isArray(animal.walks) ? animal.walks : [];
  if (species === "Cat") return Array.isArray(animal.notes) ? animal.notes : [];
  return Array.isArray(animal.walks)
    ? animal.walks
    : Array.isArray(animal.notes)
    ? animal.notes
    : [];
}

// ------------------------------
// ROW FILTER (no "Notes" column anymore)
// ------------------------------
function hasEnoughData(row) {
  const keyFields = [LOCATION_LABEL_OUT, START_LABEL, END_LABEL, "Log time", "LogType"];
  if (keyFields.some((k) => row[k] && String(row[k]).trim() !== "")) return true;

  const nonEmptyCount = Object.values(row).filter((val) => {
    if (val === null || val === undefined) return false;
    return String(val).trim() !== "";
  }).length;

  return nonEmptyCount >= 5;
}

// ------------------------------
// FETCH HELPERS
// ------------------------------
async function safeFetchJson(path) {
  try {
    const res = await fetch(path);
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json) ? json : [];
  } catch {
    return [];
  }
}

// ------------------------------
// MAIN COMPONENT
// ------------------------------
function DownloadExcelButton() {
  // ✅ Pretty popup (same design as your other popups)
  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openPopup = (title, message) => {
    setPopup({ open: true, title: title || "", message: message || "" });
  };

  const closePopup = () => {
    setPopup((p) => ({ ...p, open: false }));
  };

  async function handleDownloadExcel() {
    const pwd = prompt("Enter download password:");
    if (!pwd) return;

    // ✅ wrong password -> pretty popup (requested text)
    if (pwd !== DOWNLOAD_PASSWORD) {
      openPopup("Incorrect password", "The password is incorrect.\nPlease try again.");
      return;
    }

    const [dogs, cats] = await Promise.all([
      safeFetchJson("/api/dogs"),
      safeFetchJson("/api/cats"),
    ]);

    const animals = [
      ...dogs.map((a) => ({ ...a, __species: "Dog" })),
      ...cats.map((a) => ({ ...a, __species: "Cat" })),
    ];

    let rows = [];
    const allLabels = new Set();

    // 1) Collect ALL questionnaire labels across dog walks + cat notes
    animals.forEach((animal) => {
      const species = animal.__species || "";
      const entries = getEntries(animal, species);

      entries.forEach((entry) => {
        entry.questionnaire?.forEach((q) => {
          const qLabel = normalizeQuestionLabel(q.question);
          if (!qLabel) return;

          // exclude duration from being a column (we use it only to compute end time)
          if (DURATION_LABELS.has(qLabel)) return;

          // ✅ DO NOT create a separate "Notes" column
          if (qLabel === "Notes:" || qLabel === "Notes") return;

          allLabels.add(qLabel);
        });
      });
    });

    // Ensure system labels exist
    allLabels.add(LOCATION_LABEL_OUT);
    allLabels.add(START_LABEL);
    allLabels.add(END_LABEL);

    const labelList = [...allLabels];

    // 2) Build rows (each entry -> one row)
    animals.forEach((animal) => {
      const species = animal.__species || "";
      const animalId = animal.id || "";
      const animalName = animal.name || "";

      const entries = getEntries(animal, species);

      if (!entries.length) {
        const empty = {
          [SPECIES_LABEL]: species,
          AnimalId: animalId,
          AnimalName: animalName,
          WalkerFirstName: "",
          WalkerLastName: "",
          LogType: "",
          "Log time": "",
        };

        labelList.forEach((l) => (empty[l] = ""));

        if (hasEnoughData(empty)) rows.push(empty);
        return;
      }

      entries.forEach((entry) => {
        const datePart = getDateKeyFromEntry(entry);

        const row = {
          [SPECIES_LABEL]: species,
          AnimalId: animalId,
          AnimalName: animalName,

          WalkerFirstName: entry.userFirstName || "",
          WalkerLastName: entry.userLastName || "",

          LogType: entry.type || entry.activityType || "",

          "Log time": entry.time
            ? `${toMMDDYYYY(entry.time)}, ${new Date(entry.time).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
              })}`
            : "",
        };

        // init all questionnaire-style columns
        labelList.forEach((l) => (row[l] = ""));

        let startRaw = null;
        let durationRaw = null;

        entry.questionnaire?.forEach((q) => {
          const qLabel = normalizeQuestionLabel(q.question);
          const ans = Array.isArray(q.answer) ? q.answer.join(", ") : q.answer || "";

          if (!qLabel) return;

          if (qLabel === START_LABEL) {
            startRaw = q.answer;
            const startTime = formatTo12Hour(q.answer);
            row[START_LABEL] = datePart && startTime ? `${datePart}, ${startTime}` : startTime;
            return;
          }

          if (DURATION_LABELS.has(qLabel)) {
            durationRaw = q.answer;
            return;
          }

          if (qLabel === LOCATION_LABEL_OUT) {
            row[LOCATION_LABEL_OUT] = ans;
            return;
          }

          // ✅ ignore legacy Notes fields entirely
          if (qLabel === "Notes:" || qLabel === "Notes") return;

          row[qLabel] = ans;
        });

        const endTime = computeEndTime(startRaw, durationRaw);
        row[END_LABEL] = datePart && endTime ? `${datePart}, ${endTime}` : endTime;

        if (hasEnoughData(row)) rows.push(row);
      });
    });

    // 3) Column order (NO Notes column)
    const desiredOrder = [
      SPECIES_LABEL,
      "AnimalId",
      "AnimalName",
      "WalkerFirstName",
      "WalkerLastName",
      "LogType",
      "Log time",
      LOCATION_LABEL_OUT,
      START_LABEL,
      END_LABEL,
      SOCIAL_LABEL,
      ...labelList.filter(
        (l) =>
          l !== LOCATION_LABEL_OUT &&
          l !== START_LABEL &&
          l !== END_LABEL &&
          l !== SOCIAL_LABEL &&
          !DURATION_LABELS.has(l)
      ),
    ];

    rows = rows.map((r) =>
      Object.fromEntries(desiredOrder.filter((k) => k in r).map((k) => [k, r[k]]))
    );

    if (!rows.length) {
      const emptyRow = Object.fromEntries(desiredOrder.map((k) => [k, ""]));
      rows.push(emptyRow);
    }

    // 4) Build worksheet
    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch:
        Math.max(
          key.length,
          ...rows.map((r) => (r[key] ? String(r[key]).length : 0))
        ) + 2,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Logs");
    XLSX.writeFile(wb, "Bideawee_Logs.xlsx");
  }

  return (
    <>
      <button className="excel-button" onClick={handleDownloadExcel} type="button">
        <img
          src="https://icons.iconarchive.com/icons/blackvariant/button-ui-ms-office-2016/256/Excel-2-icon.png"
          className="excel-icon"
          alt="Excel"
        />
        Download Excel
      </button>

      {/* ✅ Pretty popup */}
      {popup.open && (
        <div className="pretty-popup-overlay" onMouseDown={closePopup}>
          <div className="pretty-popup" onMouseDown={(e) => e.stopPropagation()}>
            {popup.title ? <div className="pretty-popup-title">{popup.title}</div> : null}

            <div className="pretty-popup-body">
              {String(popup.message || "")
                .split("\n")
                .map((line, idx) => (
                  <div key={idx} className="pretty-popup-line">
                    {line === "" ? "\u00A0" : line}
                  </div>
                ))}
            </div>

            <button className="pretty-popup-close" onClick={closePopup} type="button">
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default DownloadExcelButton;
